import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { useCart } from "../../../../context/CartContext";
import {
  AddToCartRequest,
  CartType,
  ProductType,
} from "../../../../Redux/custmoer_cart_service/types";

interface FeaturedOfferSummary {
  finalPrice?: number;
  unitPrice?: number;
  availableQty?: number;
  sellerId?: string;
  sellerName?: string;
}

interface CatalogVariantSummary {
  _id: string;
  ssin: string;
  mainImage: string;
}

interface RelatedProductSummary {
  _id: string;
  name: string;
  mainImage: string;
  ssin?: string;
  description?: string;
  creatorId?: string;
  createdBy?: string;
  vendors?: { marketedBy?: string };
  featuredOffer?: FeaturedOfferSummary;
}

interface BoughtTogetherHostProduct {
  _id: string;
  name: string;
  ssin?: string;
  mainImage: string;
  description?: string;
  creatorId?: string;
  createdBy?: string;
  vendors?: { marketedBy?: string };
  featuredOffer?: FeaturedOfferSummary;
  variants?: CatalogVariantSummary[];
  boughtTogetherProducts?: RelatedProductSummary[];
}

const BoughtTogether = ({
  product,
  selectedVariation,
}: {
  product: BoughtTogetherHostProduct;
  selectedVariation: number;
}) => {
  const [selectedBoughtTogether, setSelectedBoughtTogether] = useState<
    string[]
  >([]);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { addItemToCart } = useCart();

  // Only offer in-stock items — availableQty comes from the product's featuredOffer snapshot
  const inStockBoughtTogether = (product?.boughtTogetherProducts ?? []).filter(
    (p) => (p?.featuredOffer?.availableQty ?? 0) > 0,
  );

  // Toggle product selection
  const handleSelectBoughtTogether = (productId: string) => {
    setSelectedBoughtTogether((prev) => {
      if (prev.includes(productId)) {
        // Remove if already selected
        return prev.filter((id) => id !== productId);
      } else {
        // Add if not selected
        return [...prev, productId];
      }
    });
  };

  // Check if product is selected
  const isProductSelected = (productId: string): boolean => {
    return selectedBoughtTogether.includes(productId);
  };

  // Calculate total price of selected products
  const calculateTotalPrice = (): number => {
    let total = 0;

    // Add main product price if selected
    if (isProductSelected(product?._id || "")) {
      total +=
        product?.featuredOffer?.finalPrice ||
        product?.featuredOffer?.unitPrice ||
        0;
    }

    // Add bought together products prices if selected
    inStockBoughtTogether.forEach((boughtProduct) => {
      if (isProductSelected(boughtProduct?._id || "")) {
        total +=
          boughtProduct?.featuredOffer?.finalPrice ||
          boughtProduct?.featuredOffer?.unitPrice ||
          0;
      }
    });

    return total;
  };

  // Format price with Indian currency
  const formatPrice = (price: number): string => {
    return price.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      navigate("/SignIn");
      return;
    }

    const productsToAddToCart: AddToCartRequest[] = [];

    // Main product
    if (isProductSelected(product?._id || "")) {
      const offer = product?.featuredOffer;
      const unitPrice = offer?.finalPrice || offer?.unitPrice || 0;
      if (unitPrice > 0) {
        productsToAddToCart.push({
          productId: product._id,
          productName: product.name,
          productImage: product.mainImage,
          productType: ProductType.PHYSICAL,
          variantId: product.ssin,
          quantity: 1,
          unitPrice,
          sellerId: offer?.sellerId || product.creatorId,
          sellerName: offer?.sellerName || product.vendors?.marketedBy,
          isMLMProduct: false,
          isAdminProduct: product.createdBy === "ADMIN",
        });
      }
    }

    // Bought together products
    inStockBoughtTogether.forEach((boughtProduct) => {
      if (isProductSelected(boughtProduct?._id || "")) {
        const offer = boughtProduct?.featuredOffer;
        const unitPrice = offer?.finalPrice || offer?.unitPrice || 0;
        if (unitPrice > 0) {
          productsToAddToCart.push({
            productId: boughtProduct._id,
            productName: boughtProduct.name,
            productImage: boughtProduct.mainImage,
            productType: ProductType.PHYSICAL,
            variantId: boughtProduct.ssin,
            quantity: 1,
            unitPrice,
            sellerId: offer?.sellerId || boughtProduct.creatorId,
            sellerName: offer?.sellerName || boughtProduct.vendors?.marketedBy,
            isMLMProduct: false,
          });
        }
      }
    });

    if (productsToAddToCart.length === 0) return;

    try {
      // Add all products to cart sequentially or via a batch if supported (sequentially here to match DetailPage pattern)
      for (const item of productsToAddToCart) {
        await addItemToCart(item, CartType.ECOMMERCE);
      }

      // Automatically open the side cart after adding all
      const offcanvasElement = document.getElementById("offcanvasMiniCart");
      if (offcanvasElement) {
        const bootstrap = (window as any).bootstrap;
        if (bootstrap && (window as any).bootstrap.Offcanvas) {
          const bsOffcanvas = new (window as any).bootstrap.Offcanvas(
            offcanvasElement,
          );
          bsOffcanvas.show();
        }
      }
    } catch (error) {
      console.error("Add multiple to cart error:", error);
    }
  };

  return (
    <>
      <div className=" display-max">
        <div className=" d-flex justify-content-start align-items-center gap-2 mt-3">
          <div className=" col-lg-2">
            <div className="card px-2 border-radius-0 shadow-none mb-lg-0">
              <div>
                <div className="card-header p-0 position-relative border-radius-00 z-index-2 bg-gray">
                  <div className="d-block blur-shadow-image position-relative">
                    <img
                      src={
                        product?.variants?.[selectedVariation]?.mainImage ||
                        product?.mainImage
                      }
                      alt={product?.name || ""}
                      className=" border-radius-2"
                      loading="lazy"
                      style={{
                        height: "200px",
                        // objectFit: "cover",
                        objectPosition: "center",
                        width: "100%",
                      }}
                    />

                    <input
                      type="checkbox"
                      className="border-radius-40 border-dark position-absolute top-1 end-1 "
                      style={{ width: "15px", height: "15px" }}
                      checked={isProductSelected(product?._id || "")}
                      onChange={() =>
                        handleSelectBoughtTogether(product?._id || "")
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="p-2 text-start lh-2">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <b className="font-weight-bold mt-1 mb-1">
                      {product?.name?.slice(0, 20)}...
                    </b>
                  </div>
                </div>
                {product?.description && (
                  <div className=" mt-1">
                    <div className=" lh-1">
                      <b className="mt-2 fw-normal text-xxs text-black">
                        {product.description.slice(0, 100)}...
                      </b>
                    </div>
                  </div>
                )}
              </div>
              <div className=" d-flex justify-content-between align-items-center mb-2 px-2">
                <div>
                  {(() => {
                    const unitPrice = product?.featuredOffer?.unitPrice || 0;
                    const finalPrice =
                      product?.featuredOffer?.finalPrice || 0;
                    const hasDiscount = unitPrice > finalPrice;
                    const discountPercent = hasDiscount
                      ? Math.round(((unitPrice - finalPrice) / unitPrice) * 100)
                      : 0;

                    return (
                      <b className="font-bold fs-5 text-black my-auto">
                        <sup className=" fw-normal">₹</sup>
                        {finalPrice}
                        <sub>
                          <b className="fw-normal text-black text-xxs">MRP: </b>{" "}
                          <b className="fw-normal text-black text-xxs text-decoration-line-through">
                            {unitPrice}
                          </b>
                          {hasDiscount && (
                            <b className="fw-bold text-danger text-xs ms-1">
                              ({discountPercent}%Off){" "}
                            </b>
                          )}
                        </sub>
                      </b>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
          <div className=" d-flex justify-content-start align-items-center gap-2 mt-3">
            <b className="text-black text-xxl">+</b>
          </div>
          {inStockBoughtTogether.map((boughtProduct, index) => (
            <React.Fragment key={boughtProduct?._id || index}>
              <div className=" col-lg-2">
                <div className="card px-2 border-radius-0 shadow-none mb-lg-0">
                  <div className="card-header p-0 position-relative border-radius-00 z-index-2 bg-gray">
                    <div className="d-block blur-shadow-image position-relative">
                      <img
                        src={boughtProduct?.mainImage}
                        alt={boughtProduct?.name || ""}
                        className=" border-radius-2"
                        loading="lazy"
                        style={{
                          height: "200px",
                          width: "100%",
                        }}
                      />

                      <input
                        type="checkbox"
                        className="border-radius-40 border-dark position-absolute top-1 end-1 "
                        style={{ width: "15px", height: "15px" }}
                        checked={isProductSelected(boughtProduct?._id || "")}
                        onChange={() =>
                          handleSelectBoughtTogether(boughtProduct?._id || "")
                        }
                      />
                    </div>
                  </div>
                  <div className="p-2 text-start lh-2">
                    <Link to={`/detailpage/${boughtProduct?._id}`}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <b className="font-weight-bold mt-1 mb-1">
                            {boughtProduct?.name?.slice(0, 20)}...
                          </b>
                        </div>
                      </div>
                      {boughtProduct?.description && (
                        <div className=" mt-1">
                          <div className=" lh-1">
                            <b className="mt-2 fw-normal text-xxs text-black">
                              {boughtProduct.description.slice(0, 100)}...
                            </b>
                          </div>
                        </div>
                      )}
                    </Link>
                  </div>

                  <div className=" d-flex justify-content-between align-items-center mb-2 px-2">
                    <div>
                      {(() => {
                        const unitPrice =
                          boughtProduct?.featuredOffer?.unitPrice || 0;
                        const finalPrice =
                          boughtProduct?.featuredOffer?.finalPrice || 0;
                        const hasDiscount = unitPrice > finalPrice;
                        const discountPercent = hasDiscount
                          ? Math.round(
                              ((unitPrice - finalPrice) / unitPrice) * 100,
                            )
                          : 0;

                        return (
                          <b className="font-bold fs-5 text-black my-auto">
                            <sup className=" fw-normal">₹</sup>
                            {finalPrice}
                            <sub>
                              <b className="fw-normal text-black text-xxs">
                                MRP:{" "}
                              </b>{" "}
                              <b className="fw-normal text-black text-xxs text-decoration-line-through">
                                {unitPrice}
                              </b>
                              {hasDiscount && (
                                <b className="fw-bold text-danger text-xs ms-1">
                                  ({discountPercent}%Off){" "}
                                </b>
                              )}
                            </sub>
                          </b>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              {index !== inStockBoughtTogether.length - 1 ? (
                <div className=" d-flex justify-content-start align-items-center gap-2 mt-3">
                  <b className="text-black text-xxl">+</b>
                </div>
              ) : null}
            </React.Fragment>
          ))}
          <div className=" col-lg-3">
            {selectedBoughtTogether.length > 0 ? (
              <div
                className="card px-2 border-radius-0 shadow-none mb-lg-0 "
                style={{ width: "90%" }}
              >
                <div></div>
                <div className="p-2 text-start lh-2">
                  <div className="d-flex justify-content-center align-items-center">
                    <div>
                      <b className="font-weight-bold mt-1 mb-1 text-dark">
                        Total Price: <sup className=" fw-normal">₹</sup>
                        {formatPrice(calculateTotalPrice())}
                      </b>
                    </div>
                  </div>
                  <div className=" mt-1">
                    <div className=" lh-1" style={{ width: "100%" }}>
                      <b className="mt-2 fw-normal  text-black ">
                        <button
                          className=" btn btn-sm btn-yellow w-100 border-radius-40"
                          onClick={handleAddToCart}
                        >
                          Add all {selectedBoughtTogether.length} to Cart
                        </button>
                      </b>
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="d-flex align-items-start">
                      <i
                        className="bi bi-info-circle-fill me-2"
                        style={{
                          fontSize: "16px",
                          color: "#0d6efd",
                          marginTop: "2px",
                        }}
                      ></i>
                      <p
                        className="mb-0 small text-start"
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          lineHeight: "1.4",
                        }}
                      >
                        These items are dispatched from and sold by different
                        sellers.{" "}
                        <a
                          href="#"
                          style={{ color: "#0d6efd", textDecoration: "none" }}
                        >
                          Show details
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="card px-2 border-radius-0 shadow-none mb-lg-0 "
                style={{ width: "90%" }}
              >
                <div className="p-2 text-start">
                  <div className="d-flex align-items-center justify-content-center">
                    <i className="bi bi-exclamation-circle-fill text-danger me-2"></i>
                    <span className="text-muted small">
                      Choose at least one product
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className=" display-mini display-mob">
        <div className=" mt-3 mb-3">
          <h5 className="fw-bolder mb-3 px-2">Frequently bought together</h5>

          {/* Main Product */}
          <div className=" d-flex justify-content-start align-items-center px-1 overflow-sides mb-3">
            <div className=" col-6">
              <div className="card px-1 border-radius-0 shadow-none mb-lg-0">
                <div className="card-header p-0 position-relative border-radius-00 z-index-2 bg-gray">
                  <div className="d-block blur-shadow-image position-relative">
                    <img
                      src={
                        product?.variants?.[selectedVariation]?.mainImage ||
                        product?.mainImage
                      }
                      alt={product?.name || ""}
                      className="img-fluid-mini-screens p-1"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "/placeholder-image.jpg";
                      }}
                    />
                    <input
                      type="checkbox"
                      className="border-radius-40 border-dark position-absolute top-1 end-1"
                      style={{ width: "15px", height: "15px" }}
                      checked={isProductSelected(product?._id || "")}
                      onChange={() =>
                        handleSelectBoughtTogether(product?._id || "")
                      }
                    />
                  </div>
                </div>

                <div className="p-2 text-start lh-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <b className="font-weight-bold mt-1 mb-1 text-sm">
                        {product?.name?.slice(0, 30)}...
                      </b>
                    </div>
                  </div>
                  {product?.description && (
                    <div className=" mt-1">
                      <div className=" lh-1">
                        <b className="mt-1 fw-normal text-xxs text-black">
                          {product.description.slice(0, 60)}...
                        </b>
                      </div>
                    </div>
                  )}
                </div>
                <div className=" d-flex justify-content-between align-items-end mb-2 mt-2 px-2">
                  <div className=" lh-1">
                    {(() => {
                      const unitPrice = product?.featuredOffer?.unitPrice || 0;
                      const finalPrice =
                        product?.featuredOffer?.finalPrice || 0;
                      const hasDiscount = unitPrice > finalPrice;
                      const discountPercent = hasDiscount
                        ? Math.round(
                            ((unitPrice - finalPrice) / unitPrice) * 100,
                          )
                        : 0;

                      return (
                        <>
                          <b className="font-bold fs-5 text-black my-auto">
                            <sup className=" fw-normal">₹</sup>
                            {formatPrice(finalPrice)}
                          </b>
                          <div>
                            <sub>
                              <b className="fw-normal text-black text-xxs">
                                MRP:{" "}
                              </b>{" "}
                              <b className="fw-normal text-black text-xxs text-decoration-line-through">
                                {formatPrice(unitPrice)}
                              </b>
                              {hasDiscount && (
                                <b className="fw-bold text-danger text-xs ms-1">
                                  ({discountPercent}%Off)
                                </b>
                              )}
                            </sub>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Bought Together Products */}
            {inStockBoughtTogether.map((boughtProduct, index) => {
              const unitPrice = boughtProduct?.featuredOffer?.unitPrice || 0;
              const finalPrice = boughtProduct?.featuredOffer?.finalPrice || 0;
              const hasDiscount = unitPrice > finalPrice;
              const discountPercent = hasDiscount
                ? Math.round(((unitPrice - finalPrice) / unitPrice) * 100)
                : 0;

              return (
                <div key={boughtProduct?._id || index} className=" col-6">
                  <div className="card px-1 border-radius-0 shadow-none mb-lg-0">
                    <div className="card-header p-0 position-relative border-radius-00 z-index-2 bg-gray">
                      <div className="d-block blur-shadow-image position-relative">
                        <img
                          src={boughtProduct?.mainImage}
                          alt={boughtProduct?.name || ""}
                          className="img-fluid-mini-screens p-1"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/placeholder-image.jpg";
                          }}
                        />
                        <input
                          type="checkbox"
                          className="border-radius-40 border-dark position-absolute top-1 end-1"
                          style={{ width: "15px", height: "15px" }}
                          checked={isProductSelected(boughtProduct?._id || "")}
                          onChange={() =>
                            handleSelectBoughtTogether(boughtProduct?._id || "")
                          }
                        />
                      </div>
                    </div>

                    <div className="p-2 text-start lh-0">
                      <Link to={`/detailpage/${boughtProduct?._id}`}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <b className="font-weight-bold mt-1 mb-1 text-sm">
                              {boughtProduct?.name?.slice(0, 30)}...
                            </b>
                          </div>
                        </div>
                        {boughtProduct?.description && (
                          <div className=" mt-1">
                            <div className=" lh-1">
                              <b className="mt-1 fw-normal text-xxs text-black">
                                {boughtProduct.description.slice(0, 60)}...
                              </b>
                            </div>
                          </div>
                        )}
                      </Link>
                    </div>
                    <div className=" d-flex justify-content-between align-items-end mb-2 mt-2 px-2">
                      <div className=" lh-1">
                        <b className="font-bold fs-5 text-black my-auto">
                          <sup className=" fw-normal">₹</sup>
                          {formatPrice(finalPrice)}
                        </b>
                        <div>
                          <sub>
                            <b className="fw-normal text-black text-xxs">
                              MRP:{" "}
                            </b>{" "}
                            <b className="fw-normal text-black text-xxs text-decoration-line-through">
                              {formatPrice(unitPrice)}
                            </b>
                            {hasDiscount && (
                              <b className="fw-bold text-danger text-xs ms-1">
                                ({discountPercent}%Off)
                              </b>
                            )}
                          </sub>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total Price and Add to Cart Section for Mobile */}
          {selectedBoughtTogether.length > 0 ? (
            <div className="card px-2 border-radius-0 shadow-none mb-lg-0 mx-2 mt-3">
              <div className="p-2 text-start">
                <div className="d-flex justify-content-center align-items-center mb-2">
                  <div>
                    <b className="font-weight-bold mt-1 mb-1 text-dark">
                      Total Price: <sup className=" fw-normal">₹</sup>
                      {formatPrice(calculateTotalPrice())}
                    </b>
                  </div>
                </div>
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-yellow w-100 border-radius-40"
                    onClick={handleAddToCart}
                  >
                    Add all {selectedBoughtTogether.length} to Cart
                  </button>
                </div>
                <div className="mt-2">
                  <div className="d-flex align-items-start">
                    <i
                      className="bi bi-info-circle-fill me-2"
                      style={{
                        fontSize: "14px",
                        color: "#0d6efd",
                        marginTop: "2px",
                      }}
                    ></i>
                    <p
                      className="mb-0 small text-start"
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        lineHeight: "1.4",
                      }}
                    >
                      These items are dispatched from and sold by different
                      sellers.{" "}
                      <a
                        href="#"
                        style={{ color: "#0d6efd", textDecoration: "none" }}
                      >
                        Show details
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card px-2 border-radius-0 shadow-none mb-lg-0 mx-2 mt-3">
              <div className="p-2 text-start">
                <div className="d-flex align-items-center justify-content-center">
                  <i className="bi bi-exclamation-circle-fill text-danger me-2"></i>
                  <span className="text-muted small">
                    Choose at least one product
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BoughtTogether;
